// Libraries
import React from 'react';

// Supermove
import {Loading, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useQuery, useResponsive} from '@supermove/hooks';
import {OrganizationModel, UserModel} from '@supermove/models';
import {Typography, colors} from '@supermove/styles';

// App
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';
import {RoleArgument} from '@shared/modules/User/enums/UserRole';
import AuthorizeDotNetPanel from 'modules/Organization/Settings/Company/components/AuthorizeDotNetPanel';
import BillingAdjustmentsPanel from 'modules/Organization/Settings/Company/components/BillingAdjustmentsPanel';
import CompanySettingsPage from 'modules/Organization/Settings/Company/components/CompanySettingsPage';
import CrewShowSavedCardsPanel from 'modules/Organization/Settings/Company/components/CrewShowSavedCardsPanel';
import PayEnginePanel from 'modules/Organization/Settings/Company/components/PayEnginePanel';
import PaymentMethodsSettings from 'modules/Organization/Settings/Company/components/PaymentMethodsSettings';
import StripePanel from 'modules/Organization/Settings/Company/components/StripePanel';
import TipOptionsPanel from 'modules/Organization/Settings/Company/components/TipOptionsPanel';

const Heading = Styled.Text`
  ${Typography.Responsive.PageHeading}
`;

const Description = Styled.Text`
  ${Typography.Responsive.Body}
  color: ${colors.gray.secondary};
`;

const Row = Styled.View<{index: number}>`
  z-index: ${({index}) => 100 - index};
`;

const BillingAndTips = ({
  viewingOrganization,
  refetch,
}: {
  viewingOrganization: OrganizationModel;
  refetch: () => void;
}) => {
  const responsive = useResponsive();
  return (
    <React.Fragment>
      <Heading responsive={responsive}>Billing & Tips (Crew App)</Heading>
      <Space height={16} />
      <Description responsive={responsive}>
        Manage the options shown on the Crew App when customers are making a payment.
      </Description>
      <Space height={24} />
      <BillingAdjustmentsPanel
        organization={viewingOrganization}
        refetch={refetch}
        index={0}
        isViewingChildBranch={!viewingOrganization.isPrimary}
      />
      <Space height={24} />
      <CrewShowSavedCardsPanel organization={viewingOrganization} index={1} />
      <Space height={24} />
      <TipOptionsPanel
        organization={viewingOrganization}
        index={2}
        isViewingChildBranch={!viewingOrganization.isPrimary}
        refetch={refetch}
      />
    </React.Fragment>
  );
};

const Processors = ({
  viewingOrganization,
  role,
}: {
  viewingOrganization: OrganizationModel;
  role: RoleArgument;
}) => {
  const responsive = useResponsive();
  const {isAuthorizeDotNetPayengineGatewayEnabled, isStripePayengineGatewayEnabled} =
    viewingOrganization.settings;
  return (
    <React.Fragment>
      <Heading responsive={responsive}>Processors</Heading>
      <Space height={16} />
      <Description responsive={responsive}>
        Configure the payment processors used on Supermove.
      </Description>
      <Space height={24} />
      <Row index={0}>
        <PayEnginePanel organization={viewingOrganization} role={role} />
      </Row>
      {isStripePayengineGatewayEnabled && (
        <React.Fragment>
          <Space height={24} />
          <Row index={1}>
            <StripePanel
              organization={viewingOrganization}
              redirectPath={'/settings/company/payments'}
            />
          </Row>
        </React.Fragment>
      )}
      {isAuthorizeDotNetPayengineGatewayEnabled && (
        <React.Fragment>
          <Space height={24} />
          <Row index={2}>
            <AuthorizeDotNetPanel organization={viewingOrganization} />
          </Row>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

const OrganizationSettingsCompanyPaymentsPageContent = ({
  viewer,
  refetch,
}: {
  viewer: UserModel;
  refetch: () => void;
}) => {
  const {viewingOrganization, role} = viewer;
  return (
    <React.Fragment>
      <PaymentMethodsSettings viewingOrganization={viewingOrganization} refetch={refetch} />
      <Space height={32} />
      <BillingAndTips viewingOrganization={viewingOrganization} refetch={refetch} />
      <Space height={32} />
      <Processors viewingOrganization={viewingOrganization} role={role} />
      <Space height={32} />
    </React.Fragment>
  );
};

const OrganizationSettingsCompanyPaymentsPage = () => {
  const {loading, data, refetch} = useQuery(OrganizationSettingsCompanyPaymentsPage.query, {
    fetchPolicy: 'cache-and-network',
  });

  return (
    <CompanySettingsPage
      title={'Payments'}
      description={'Manage payment methods, fees, tips, and payment processors.'}
    >
      <Loading loading={loading} as={PageLoadingIndicator}>
        {() => (
          <OrganizationSettingsCompanyPaymentsPageContent viewer={data?.viewer} refetch={refetch} />
        )}
      </Loading>
    </CompanySettingsPage>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
OrganizationSettingsCompanyPaymentsPage.query = gql`
  ${AuthorizeDotNetPanel.fragment}
  ${BillingAdjustmentsPanel.fragment}
  ${CrewShowSavedCardsPanel.fragment}
  ${PayEnginePanel.fragment}
  ${PaymentMethodsSettings.fragment}
  ${StripePanel.fragment}
  ${TipOptionsPanel.fragment}
  query OrganizationSettingsCompanyPaymentsPage {
    ${gql.query}
    viewer {
      id
      role
      viewingOrganization {
        id
        isPrimary
        settings {
          id
          isStripePayengineGatewayEnabled
          isAuthorizeDotNetPayengineGatewayEnabled
        }
        ...BillingAdjustmentsPanel
        ...CrewShowSavedCardsPanel
        ...StripePanel
        ...PayEnginePanel
        ...PaymentMethodsSettings
        ...AuthorizeDotNetPanel
        ...TipOptionsPanel
      }
    }
  }
`;

export default OrganizationSettingsCompanyPaymentsPage;
